import { useMemo } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { Activity, Trophy, Target, TrendingUp, Percent, Zap } from 'lucide-react-native';

import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Skeleton } from './ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiRequestJson } from '../lib/queryClient';
import { trpcClient, useTRPC } from '../lib/trpc';
import { clientLogger } from '../lib/datadog';
import { colors, typography } from '@/lib/theme';

const transparentColor = 'transparent';

interface UserStatsProps {
  leagueId: string;
  seasonId?: string | null;
  scoringSystem?: 'oddsBased' | 'totalWins';
}

interface UserStatsData {
  totalPoints: number;
  weeklyAverage: number;
  weeklyStdDev: number;
  winPercentage: number;
  correctPicks: number;
  totalPicks: number;
  upsetPicks: {
    correct: number;
    total: number;
  };
  bestWeek?: {
    week: number;
    points: number;
  };
}

interface LeagueSeasonWeekOption {
  id: string;
  leagueWeekId: string;
  label: string;
  displayLabel: string;
  weekNumber: number | null;
  startsAt: string | null;
  endsAt: string | null;
}

interface LeagueSeasonWeeksPayload {
  resolvedSeasonId: string;
  weeks: LeagueSeasonWeekOption[];
}

export default function UserStats({ leagueId, seasonId, scoringSystem = 'oddsBased' }: UserStatsProps) {
  const { user } = useAuth();
  const trpc = useTRPC();

  const { data: stats, isLoading } = useQuery<UserStatsData>(
    trpc.users.stats.queryOptions(
      {
        userId: user?.id ?? '',
        leagueId,
        seasonId: seasonId ?? undefined,
      },
      { enabled: !!user?.id && !!leagueId && !!seasonId },
    ) as any,
  );

  const {
    data: seasonWeeks,
    isLoading: seasonWeeksLoading,
  } = useQuery<LeagueSeasonWeeksPayload | null>({
    queryKey: ['/api/leagues', leagueId, 'season-weeks', seasonId ?? 'none', 'stats'],
    enabled: !!leagueId && !!seasonId,
    queryFn: async () => {
      if (!leagueId || !seasonId) {
        return null;
      }
      const params = new URLSearchParams();
      params.set('seasonId', seasonId);
      try {
        return await apiRequestJson<LeagueSeasonWeeksPayload>(
          `/api/leagues/${leagueId}/weeks?${params.toString()}`,
          'GET',
        );
      } catch (error) {
        if (__DEV__) {
          clientLogger.warn('Falling back to legacy week list for User Stats', { error });
        }
        const fallbackWeeks = await trpcClient.nfl.weeks.query();
        return {
          resolvedSeasonId: seasonId,
          weeks: fallbackWeeks.map((week: any, index: number) => ({
            id: week.id,
            leagueWeekId: week.id,
            label: week.label || week.id,
            displayLabel:
              typeof week.week === 'number' ? `Week ${week.week}` : week.label || `Week ${index + 1}`,
            weekNumber: typeof week.week === 'number' ? week.week : index + 1,
            startsAt: week.startDate ?? week.startsAt ?? null,
            endsAt: week.endDate ?? week.endsAt ?? null,
          })),
        } satisfies LeagueSeasonWeeksPayload;
      }
    },
  });

  const seasonWeekOptions = useMemo(() => seasonWeeks?.weeks ?? [], [seasonWeeks]);

  const bestWeekLabel = useMemo(() => {
    if (!stats?.bestWeek) {
      return null;
    }
    const matchedWeek =
      seasonWeekOptions.find(
        (week) => typeof week.weekNumber === 'number' && week.weekNumber === stats.bestWeek?.week,
      ) ?? null;
    if (matchedWeek) {
      return matchedWeek.displayLabel;
    }
    return typeof stats.bestWeek.week === 'number'
      ? `Week ${stats.bestWeek.week}`
      : 'Best Week';
  }, [seasonWeekOptions, stats?.bestWeek]);

  const isLoadingState = isLoading || seasonWeeksLoading;

  if (isLoadingState) {
    return (
      <Card style={styles.transparentCard}>
        <CardHeader style={styles.headerCompact}>
          <CardTitle style={styles.cardTitle}>
            <Trophy size={20} color={colors.text.primary} />
            <Text style={styles.titleText}>Your Season Stats</Text>
          </CardTitle>
        </CardHeader>
        <CardContent style={styles.contentNoPadding}>
          <View style={styles.skeletonContainer}>
            {[1, 2, 3, 4, 5].map((i) => (
              <View key={i} style={styles.skeletonRow}>
                <Skeleton width={96} height={16} />
                <Skeleton width={64} height={16} />
              </View>
            ))}
          </View>
        </CardContent>
      </Card>
    );
  }

  if (!stats || stats.totalPicks === 0) {
    return (
      <Card style={styles.transparentCard}>
        <CardHeader style={styles.headerCompact}>
          <CardTitle style={styles.cardTitle}>
            <Trophy size={20} color={colors.text.primary} />
            <Text style={styles.titleText}>Your Season Stats</Text>
          </CardTitle>
        </CardHeader>
        <CardContent style={styles.contentNoPadding}>
          <View style={styles.emptyContainer}>
            <Target size={48} color={colors.text.secondary} />
            <Text style={styles.emptyTitle}>No picks yet</Text>
            <Text style={styles.emptySubtext}>Make some picks to see your stats!</Text>
          </View>
        </CardContent>
      </Card>
    );
  }

  const formatPointsValue = (value: number): string => {
    if (!Number.isFinite(value)) {
      return scoringSystem === 'oddsBased' ? '0.0' : '0';
    }
    return scoringSystem === 'oddsBased' ? value.toFixed(1) : Math.round(value).toString();
  };

  const totalPointsLabel = scoringSystem === 'totalWins' ? 'Total Wins' : 'Total Points';
  const weeklyAverageLabel = scoringSystem === 'totalWins' ? 'Avg Wins' : 'Weekly Average';
  const weeklyStdDevLabel = 'Weekly Std Dev';

  return (
    <Card style={styles.transparentCard}>
      <CardHeader style={styles.headerCompact}>
        <CardTitle style={styles.cardTitle}>
          <Trophy size={20} color={colors.text.primary} />
          <Text style={styles.titleText}>Your Season Stats</Text>
        </CardTitle>
      </CardHeader>
      <CardContent style={styles.contentNoPadding}>
        <View style={styles.statsContainer}>
          {/* Stats Grid */}
          <View style={styles.statsGrid}>
            {/* Total Points */}
            <View style={[styles.statCard, styles.statCardBlue]}>
              <View style={[styles.iconContainer, styles.iconContainerBlue]}>
                <Trophy size={20} color={colors.text.primary} />
              </View>
              <View style={styles.statContent}>
                <Text style={styles.statLabel}>{totalPointsLabel}</Text>
                <Text style={[styles.statValue, styles.statValueBlue]}>
                  {typeof stats.totalPoints === 'number'
                    ? formatPointsValue(stats.totalPoints)
                    : scoringSystem === 'oddsBased'
                      ? '0.0'
                      : '0'}
                </Text>
              </View>
            </View>

            {/* Weekly Average */}
            <View style={[styles.statCard, styles.statCardGreen]}>
              <View style={[styles.iconContainer, styles.iconContainerGreen]}>
                <TrendingUp size={20} color={colors.success[700]} />
              </View>
              <View style={styles.statContent}>
                <Text style={styles.statLabel}>{weeklyAverageLabel}</Text>
                <Text style={[styles.statValue, styles.statValueGreen]}>
                  {typeof stats.weeklyAverage === 'number'
                    ? formatPointsValue(stats.weeklyAverage)
                    : scoringSystem === 'oddsBased'
                      ? '0.0'
                      : '0'}
                </Text>
              </View>
            </View>

            {/* Weekly Standard Deviation */}
            <View style={[styles.statCard, styles.statCardTeal]}>
              <View style={[styles.iconContainer, styles.iconContainerTeal]}>
                <Activity size={20} color={colors.text.primary} />
              </View>
              <View style={styles.statContent}>
                <Text style={styles.statLabel}>{weeklyStdDevLabel}</Text>
                <Text style={[styles.statValue, styles.statValueTeal]}>
                  {typeof stats.weeklyStdDev === 'number'
                    ? formatPointsValue(stats.weeklyStdDev)
                    : scoringSystem === 'oddsBased'
                      ? '0.0'
                      : '0'}
                </Text>
              </View>
            </View>

            {/* Win Percentage */}
            <View style={[styles.statCard, styles.statCardPurple]}>
              <View style={[styles.iconContainer, styles.iconContainerPurple]}>
                <Percent size={20} color={colors.text.primary} />
              </View>
              <View style={styles.statContent}>
                <Text style={styles.statLabel}>Win Percentage</Text>
                <Text style={[styles.statValue, styles.statValuePurple]}>
                  {typeof stats.winPercentage === 'number'
                    ? `${stats.winPercentage}%`
                    : '0%'}
                </Text>
                <Text style={styles.statSubtext}>
                  {stats.correctPicks}/{stats.correctPicks + (stats.totalPicks - stats.correctPicks)}{' '}
                  correct
                </Text>
              </View>
            </View>

            {/* Upset Picks */}
            <View style={[styles.statCard, styles.statCardOrange]}>
              <View style={[styles.iconContainer, styles.iconContainerOrange]}>
                <Zap size={20} color={colors.warning[800]} />
              </View>
              <View style={styles.statContent}>
                <Text style={styles.statLabel}>Successful Upset Picks</Text>
                <Text style={[styles.statValue, styles.statValueOrange]}>
                  {stats.upsetPicks.correct}/{stats.upsetPicks.total}
                </Text>
                <Text style={styles.statSubtext}>
                  {stats.upsetPicks.total > 0
                    ? `${Math.round((stats.upsetPicks.correct / stats.upsetPicks.total) * 100)}% success rate`
                    : 'No upset picks yet'}
                </Text>
              </View>
            </View>
          </View>

          {/* Best Week */}
          {stats.bestWeek && (
            <View style={styles.bestWeekContainer}>
              <View style={[styles.statCard, styles.statCardYellow, styles.bestWeekCard]}>
                <View style={styles.bestWeekContent}>
                  <View>
                    <Text style={styles.bestWeekLabel}>Best Week Performance</Text>
                    <Text style={[styles.statValue, styles.statValueYellow]}>
                      {bestWeekLabel || `Week ${stats.bestWeek.week}`}
                    </Text>
                  </View>
                  <View style={styles.bestWeekRight}>
                    <Text style={[styles.bestWeekPoints, styles.statValueYellow]}>
                      {formatPointsValue(stats.bestWeek.points)}
                    </Text>
                    <Text style={styles.bestWeekPointsLabel}>
                      {scoringSystem === 'totalWins' ? 'wins' : 'points'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          )}
        </View>
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create(() => ({
  bestWeekCard: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: 24,
    width: '100%',
  },
  bestWeekContainer: {
    paddingTop: 16,
  },
  bestWeekContent: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bestWeekLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  bestWeekPoints: {
    fontFamily: typography.fontFamily.display,
    fontSize: 36,
    fontWeight: '900',
    textAlign: 'right',
  },
  bestWeekPointsLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    textAlign: 'right',
    textTransform: 'uppercase',
  },
  bestWeekRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  cardTitle: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  contentNoPadding: {
    paddingHorizontal: 0,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptySubtext: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    marginTop: 4,
  },
  emptyTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
  },
  headerCompact: {
    paddingHorizontal: 0,
    paddingVertical: 8,
  },
  iconContainer: {
    alignItems: 'center',
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  iconContainerBlue: {
    backgroundColor: colors.ink.acid,
  },
  iconContainerGreen: {
    backgroundColor: colors.success[100],
  },
  iconContainerOrange: {
    backgroundColor: colors.warning[100],
  },
  iconContainerPurple: {
    backgroundColor: colors.ink.paperDark,
  },
  iconContainerTeal: {
    backgroundColor: colors.ink.paperDark,
  },
  skeletonContainer: {
    gap: 16,
  },
  skeletonRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statCard: {
    alignItems: 'flex-start',
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    elevation: 2,
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: '48%',
  },
  statCardBlue: {
    backgroundColor: colors.ink.acid,
  },
  statCardGreen: {
    backgroundColor: colors.success[50],
  },
  statCardOrange: {
    backgroundColor: colors.warning[50],
  },
  statCardPurple: {
    backgroundColor: colors.ink.paperDark,
  },
  statCardTeal: {
    backgroundColor: colors.ink.paperDark,
  },
  statCardYellow: {
    backgroundColor: colors.warning[50],
  },
  statContent: {
    width: '100%',
  },
  statLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  statSubtext: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    marginTop: 2,
    textTransform: 'uppercase',
  },
  statValue: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 24,
    fontWeight: '900',
  },
  statValueBlue: {
    color: colors.text.primary,
  },
  statValueGreen: {
    color: colors.success[700],
  },
  statValueOrange: {
    color: colors.warning[800],
  },
  statValuePurple: {
    color: colors.text.primary,
  },
  statValueTeal: {
    color: colors.text.primary,
  },
  statValueYellow: {
    color: colors.warning[800],
  },
  statsContainer: {
    gap: 24,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  titleText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  transparentCard: {
    backgroundColor: transparentColor,
    borderWidth: 0,
    elevation: 0,
    shadowOpacity: 0,
  },
}));
